/**
 * Canvas file drops are handled very differently to other canvas drops & other file drops.
 * If the canvas is empty when dropping files, the files / images will be layed out in a
 * masonry manner, based on the width of the current window.
 *
 * This requires an asynchronous addition of the files / images so we can determine the size
 * of the files after each one is created.
 *
 * An improvement to this would be to detect if the board is currently in a masonry style
 * alignment, and then continue to add the files in the masonry alignment if so, however
 * that's a far bigger task, so will be left for when auto-alignment is tackled.
 */
// Lib
import { isEmpty } from 'lodash';

// Lib Types
import { DropTargetMonitor } from 'react-dnd/lib/interfaces/monitors';
import { ReactInstance } from 'react';

// Utils
import { getWindowWidth } from '../../utils/domUtil';
import { pixelsToNextGridPoints } from '../../utils/grid/gridUtils';
import { getNativeLocationOnCanvas } from './canvasDropTargetDecorator';
import { getDomElementId } from '../../element/utils/elementUtil';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';
import FileValidationError from '../../../common/error/FileValidationError';
import delay from '../../../common/utils/lib/delay';

// Types
import { Point } from '../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';
import { MNElementLocation } from '../../../common/elements/elementModelTypes';

// Constants
import { ELEMENT_CREATION_SOURCES, ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { BoardSections } from '../../../common/boards/boardConstants';

const DEFAULT_ELEMENT_GAP = 2;

// TODO: Remove this once Canvas.js has been moved to TS, and Canvas props are defined there
interface CanvasProps {
    currentBoardId: string;
    canvasElementIds: Array<string>;
    gridSize: number;
    zoomScale: number;
    zoomTranslationPx: Point;
    canvasSize: RectSize;
    isEditable: boolean;
    createShortcutFile: {
        (options: any): Promise<string>;
    };
}

interface CanvasFileDropState {
    boardSection: BoardSections.CANVAS;
    previousElementId: string | null;
    addedElementIds: Array<string>;

    currentColumn: number;
    columns: Array<number>;
    startingPoint: Point;
    columnsCount: number;
}

interface InboxFileDropState {
    boardSection: BoardSections.INBOX;
    previousElementId: string | null;
    addedElementIds: Array<string>;
}

type FileDropState = InboxFileDropState | CanvasFileDropState;

/**
 * Attempt to lay files out in columns if there's no elements on the canvas when dropping files.
 */
const getFileLocationOnCanvas = (props: CanvasProps, state: CanvasFileDropState): MNElementLocation => {
    const { currentColumn, columns, startingPoint } = state;
    const { currentBoardId } = props;

    // xGap and yGap check if the board is not empty and we're in the first row or column,
    //  - in this case, use a gap of x = 0 and/or y = 1 so it can be close to the cursor,
    //  - if empty or not the first row or column, use default gap size
    const xGap = currentColumn === 0 && startingPoint.x !== 0 ? 0 : DEFAULT_ELEMENT_GAP;
    const yGap = columns[currentColumn] === 0 && startingPoint.y !== 0 ? 1 : DEFAULT_ELEMENT_GAP;

    // When the board is empty, we need to add 2 grid units to account for the shift off the canvas document boundary
    // Otherwise, the elements are positioned to the grid point to the left of the cursor and we instead want it
    //  to be on/to the right of the cursor, so we shift it by 1
    const xShift = startingPoint.x === 0 ? 2 : 1;

    const x = startingPoint.x + xGap * currentColumn + ELEMENT_DEFAULT_WIDTH * currentColumn + xShift;
    const y = startingPoint.y + columns[currentColumn] + yGap;

    return {
        parentId: currentBoardId,
        section: BoardSections.CANVAS,
        position: {
            x,
            y,
        },
    };
};

const getFileLocationOnInbox = (props: CanvasProps, index: number): MNElementLocation => {
    const { currentBoardId } = props;

    return {
        parentId: currentBoardId,
        section: BoardSections.INBOX,
        position: { index },
    };
};

const getMousePointerClickPosition = (props: CanvasProps, monitor: DropTargetMonitor, domNode: HTMLElement): Point => {
    const location = getNativeLocationOnCanvas(props, monitor, domNode);

    return { x: location?.position?.x || 0, y: location?.position?.y || 0 };
};

const getInitialFileDropState = (
    props: CanvasProps,
    monitor: DropTargetMonitor,
    domNode: HTMLElement,
): FileDropState => {
    const { gridSize, canvasElementIds, canvasSize, zoomTranslationPx } = props;

    const isMultipleItems = monitor.getItem()?.files?.length > 1;
    const isEmptyCanvas = isEmpty(canvasElementIds);

    // If dropping multiple items on an empty canvas, place in grids starting from { x: 0, y: 0 }.
    // Otherwise, always drop at the mouse pointer
    const startingPoint =
        isEmptyCanvas && isMultipleItems ? { x: 0, y: 0 } : getMousePointerClickPosition(props, monitor, domNode);

    let gridContainerWidthPx = canvasSize.width - startingPoint.x * gridSize;

    // If user is zoomed in, set the grid container width to be equal to the canvas viewport at 100%.
    // This is so that if the user zoom to 100%, the files will be perfectly contained within the canvas viewport.
    if (zoomTranslationPx.x === 0 && zoomTranslationPx.y === 0) {
        gridContainerWidthPx = measurementsRegistry.getCanvasViewportDomElement()
            ? measurementsRegistry.getCanvasViewportDomElement().getBoundingClientRect().width
            : getWindowWidth();
    }

    const columnWidthPx = (DEFAULT_ELEMENT_GAP + ELEMENT_DEFAULT_WIDTH) * gridSize;
    const columnsCount = Math.max(3, Math.floor(gridContainerWidthPx / columnWidthPx));

    const columns = [];

    for (let i = 0; i < columnsCount; i++) {
        columns.push(0);
    }

    return {
        boardSection: BoardSections.CANVAS,
        previousElementId: null,
        addedElementIds: [],
        startingPoint,
        currentColumn: 0,
        columns,
        columnsCount,
    };
};

const updateCanvasFileDropState = (props: CanvasProps, state: CanvasFileDropState) => {
    const { gridSize, zoomScale } = props;
    const { previousElementId, currentColumn, columns, columnsCount, startingPoint } = state;

    if (!previousElementId) return state;

    // NOTE: Unfortunately we have to get the DOM measurement, rather than the syncedMeasurementsSingleton
    //  because the singleton isn't updated yet
    const node = document.getElementById(getDomElementId(previousElementId));
    const previousElementRect = node?.getBoundingClientRect();

    if (!previousElementRect) return state;

    // TODO-ZOOM: There is a bug now where the height of the element does not get set correctly
    //  if we zoom in/out while files are being uploaded
    const previousElementHeightPx = previousElementRect.height / zoomScale || 0;
    const previousElementHeightGridPoints = pixelsToNextGridPoints(previousElementHeightPx, gridSize);

    // yGap checks if the board is not empty and we're in the first column,
    //  - in this case, use a gap of y = 1 so it can be close to the cursor,
    //  - if empty or not the first row or column, use default gap size
    const yGap = columns[currentColumn] === 0 && startingPoint.y !== 0 ? 1 : DEFAULT_ELEMENT_GAP;

    // The previous element was added to the current column, so the new height of the column
    // is the previous column height + the gap + the previously added element height
    columns[currentColumn] = columns[currentColumn] + yGap + previousElementHeightGridPoints;

    // Now find the new smallest column to add the next element to
    let currentMin = Infinity;

    for (let i = 0; i < columnsCount; i++) {
        const columnHeight = columns[i];

        if (columnHeight < currentMin) {
            state.currentColumn = i;
            currentMin = columnHeight;
        }
    }

    return state;
};

const createFile = async (
    props: CanvasProps,
    file: File,
    index: number,
    state: FileDropState,
): Promise<FileDropState> => {
    const { createShortcutFile, currentBoardId } = props;

    let newState = state;
    let location;

    try {
        // Now that the Image element creation is async, we need to wait for the previous element to be created
        // this introduces a defer, allowing the previous element to be rendered in the next CPU cycle.
        // Once it's rendered, we can get the DOM measurements and calculate the next element's position
        await delay(0);

        if (newState.boardSection === BoardSections.CANVAS) {
            newState = updateCanvasFileDropState(props, newState);
            location = getFileLocationOnCanvas(props, newState);
        } else {
            location = getFileLocationOnInbox(props, index);
        }

        const fileElementId = await createShortcutFile({
            location,
            file,
            currentBoardId,
            creationSource: ELEMENT_CREATION_SOURCES.DROPPED_ONTO_WINDOW,
            select: false,
        });

        newState.previousElementId = fileElementId;
        newState.addedElementIds.push(fileElementId);
    } catch (err: unknown) {
        if (err instanceof FileValidationError) {
            throw err;
        }
    }

    return newState;
};

const createFiles = async (props: CanvasProps, monitor: DropTargetMonitor, domNode: HTMLElement) => {
    const { files } = monitor.getItem();

    let state = getInitialFileDropState(props, monitor, domNode);

    for (const i in files) {
        const index = parseInt(i, 10);
        const file = files[index];
        if (!file) continue;

        // eslint-disable-next-line no-await-in-loop
        state = await createFile(props, file, index, state);
    }
};

interface HandleDropResult {}

/**
 * See the comment at the top of this file.
 * This is a unique use case, so it cannot use the standard drop handler methods.
 */
export const handleCanvasFileDrop = (
    props: CanvasProps,
    monitor: DropTargetMonitor,
    domNode: HTMLElement,
): HandleDropResult | undefined => {
    const { isEditable } = props;

    if (!isEditable) return;

    // This is an async function, but we want to respond synchronously here to maintain the React DnD API
    createFiles(props, monitor, domNode);

    return {};
};
